import type {Poster} from '@PosterWhiteboard/poster/poster.class';
import type {Page} from '@PosterWhiteboard/page/page.class';
import type {TempImageData} from '@Libraries/add-media-library';
import {isTempImageData} from '@Libraries/add-media-library';
import type {FileUploadMethods, UploadFileDataTypes} from '@Libraries/user-media-library';
import {uploadFiles} from '@Libraries/user-media-library';
import {getDeferredEditorUploads, removeDeferredEditorUploads} from '@Libraries/deferred-editor-uploads.library';
import {loadImageAsync} from '@Utils/image.util';
import {getContainCenteredPlacement} from '@Utils/math.util';
import {noop} from '@Utils/general.util';

export const consumeDeferredEditorUploads = async (poster: Poster): Promise<void> => {
  const files = await getDeferredEditorUploads(poster.hashedID);
  if (!files || files.length === 0) {
    return;
  }

  // Only drop the stored uploads once the images are actually on the pages; otherwise a crash
  // or a closed tab mid-add would lose the files with the poster left blank and no retry.
  const pageIds = poster.pages.pageOrder;
  for (let index = 0; index < files.length && index < pageIds.length; index++) {
    const page = poster.pages.getPage(pageIds[index]);
    if (!page) {
      continue;
    }
    try {
      // eslint-disable-next-line no-await-in-loop
      await addDeferredUploadToPage(page, files[index]);
    } catch (e) {
      console.error(e);
    }
  }

  await removeDeferredEditorUploads(poster.hashedID);
};

const addDeferredUploadToPage = async (page: Page, file: File): Promise<void> => {
  const tempImageData = await uploadFileForTempImage(file);
  if (!tempImageData) {
    return;
  }

  const image = await loadImageAsync(tempImageData.dataUrl);
  const placement = getContainCenteredPlacement(page.poster.width, page.poster.height, image.naturalWidth, image.naturalHeight);

  await page.items.addItems.addNewItems(
    [
      {
        ...tempImageData,
        ...placement,
      },
    ],
    {selectOnAdd: false}
  );
};

const uploadFileForTempImage = async (file: File): Promise<TempImageData | null> => {
  return new Promise<TempImageData | null>((resolve) => {
    let settled = false;
    const settle = (value: TempImageData | null): void => {
      if (settled) {
        return;
      }
      settled = true;
      resolve(value);
    };

    const methods: FileUploadMethods = {
      onFileUpload: (_type, data: UploadFileDataTypes): void => {
        settle(getTempImageDataFromUploadResult(data));
      },
      onUploadError: (): void => {
        settle(null);
      },
      onAfterUpload: noop,
    };

    void uploadFiles(methods, [file], {shouldLoadDataUrlImage: true});
  });
};

const getTempImageDataFromUploadResult = (data: UploadFileDataTypes): TempImageData | null => {
  if (Array.isArray(data)) {
    return data.find(isTempImageData) ?? null;
  }
  return isTempImageData(data) ? data : null;
};
